import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { publishPlatformEvent } from '@/platform/events';
import {
  ConfigurationContext,
  type ConfigurationContextValue,
} from '@/configuration/ConfigurationContext';
import { useAuth } from '@/auth';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { configurationService } from '@/configuration/ConfigurationService';
import type {
  BrandingSettings,
  GeneralSettings,
  NotificationSettings,
  SecuritySettings,
  SystemSettings,
} from '@/configuration/ConfigurationTypes';

interface ConfigurationProviderProps {
  children: ReactNode;
}

const configurationModuleIds = new Set(['system-settings']);

const getAdminModuleId = (pathname: string): string | null => {
  const match = pathname.match(/^\/admin(?:\/embed)?\/([^/]+)/);
  return match?.[1] ?? null;
};

export function ConfigurationProvider({
  children,
}: ConfigurationProviderProps) {
  const { status } = useAuth();
  const { t } = useTranslation('entity-portal');
  const { pathname } = useLocation();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loaded = await configurationService.getSystemSettings();
      setSettings(loaded);
    } catch {
      setError(t('error.configLoadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const moduleId = getAdminModuleId(pathname);
    const shouldLoadConfiguration =
      moduleId !== null && configurationModuleIds.has(moduleId);

    if (status !== 'authenticated' || !shouldLoadConfiguration) {
      setSettings(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    void refresh();
  }, [pathname, refresh, status]);

  const emitConfigurationEvent = useCallback(
    (details: string, section: string, previousVersion: number) => {
      publishPlatformEvent({
        type: 'API_CONFIGURED',
        category: 'configuration',
        actor: {
          displayName: 'System Process',
          actorType: 'system',
        },
        resource: 'systemSettings',
        details,
        severity: 'medium',
        environment: import.meta.env.MODE ?? 'local',
        product: 'Global Portal',
        ipAddress: '127.0.0.1',
        metadata: {
          section,
          previousVersion,
        },
      });
    },
    [],
  );

  const updateGeneral = useCallback(
    async (payload: GeneralSettings) => {
      const previousVersion = settings?.version ?? 0;
      const next = await configurationService.updateGeneralSettings(payload);
      setSettings(next);
      emitConfigurationEvent('Portal Name Updated', 'general', previousVersion);
    },
    [emitConfigurationEvent, settings?.version],
  );

  const updateNotifications = useCallback(
    async (payload: NotificationSettings) => {
      const previousVersion = settings?.version ?? 0;
      const next =
        await configurationService.updateNotificationSettings(payload);
      setSettings(next);
      emitConfigurationEvent(
        'Webhook Changed',
        'notifications',
        previousVersion,
      );
    },
    [emitConfigurationEvent, settings?.version],
  );

  const updateSecurity = useCallback(
    async (payload: SecuritySettings) => {
      const previousVersion = settings?.version ?? 0;
      const next = await configurationService.updateSecuritySettings(payload);
      setSettings(next);
      emitConfigurationEvent(
        'Password Policy Updated',
        'security',
        previousVersion,
      );
    },
    [emitConfigurationEvent, settings?.version],
  );

  const updateBranding = useCallback(
    async (payload: BrandingSettings) => {
      const previousVersion = settings?.version ?? 0;
      const next = await configurationService.updateBrandingSettings(payload);
      setSettings(next);
      emitConfigurationEvent('Brand Logo Changed', 'branding', previousVersion);
    },
    [emitConfigurationEvent, settings?.version],
  );

  const value: ConfigurationContextValue = useMemo(
    () => ({
      settings,
      isLoading,
      error,
      refresh,
      updateGeneral,
      updateNotifications,
      updateSecurity,
      updateBranding,
    }),
    [
      error,
      isLoading,
      refresh,
      settings,
      updateBranding,
      updateGeneral,
      updateNotifications,
      updateSecurity,
    ],
  );

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
}
